import { Input, BlobSource, ALL_FORMATS, Output, Mp4OutputFormat, BufferTarget, Conversion } from 'mediabunny'

// MediaRecorder (mediaRecorderFallback.js) records whatever container/codec the browser is willing
// to produce - on Chrome/Firefox/Edge that's video/webm (vp9 or vp8 + opus), since none of them can
// record mp4 despite being able to decode/play it. This takes that raw capture and re-muxes/
// transcodes it into a real .mp4 via mediabunny's Conversion (WebCodecs-backed), so the fallback
// pipeline's output is just as reliably .mp4 as the main pipeline (exportTimeline.js).
export const normalizeToMp4 = async (blob, { videoBitrate, audioBitrate } = {}) => {
    if (blob.type.includes('mp4')) return blob // MediaRecorder already produced mp4 (e.g. Safari) - nothing to do

    const attempt = async (video, audio) => {
        const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(blob) })
        const output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() })
        const conversion = await Conversion.init({ input, output, video, audio, showWarnings: false })
        if (!conversion.isValid) return null
        // mp4 allows 0 video tracks - isValid alone doesn't guarantee a video track survived.
        if (!conversion.utilizedTracks.some(t => t.type === 'video')) return null
        await conversion.execute()
        return new Blob([output.target.buffer], { type: 'video/mp4' })
    }

    try {
        // h264/aac - matches the main pipeline, most broadly compatible with social networks.
        const strict = await attempt(
            { codec: 'avc', bitrate: videoBitrate },
            { codec: 'aac', bitrate: audioBitrate }
        )
        if (strict) return strict

        // avc unavailable - fall back to vp9 only, not "any encodable codec" (that can land on
        // av1/hevc, which several social networks reject even inside a valid mp4).
        const vp9 = await attempt(
            { codec: 'vp9', bitrate: videoBitrate },
            { bitrate: audioBitrate }
        )
        if (vp9) return vp9
    } catch (e) {
        // fall through to the raw capture below
    }

    // No trusted codec encodable - return the raw webm as-is (mediaExportJobs.js already
    // handles a non-mp4 blob correctly).
    return blob
}
